import React, { useRef, useEffect, useState, useContext } from "react";
import { useIntl } from "react-intl";

import Video, { createLocalAudioTrack, LocalDataTrack } from "twilio-video";
import { Client as ConversationsClient } from "@twilio/conversations";

import AuditService from "../../services/audit-service";
import { RemoteAuditContext } from "../../globalContext/remoteAudit/remoteAuditProvider";
import {
  setAudioStatus,
  setVideoStatus,
  setConnectedRoom,
  setCurrentQuestion,
  setDnsdSid,
  setIsEvidenceCompleted,
  setCallEnded,
  setQuestionPayloadInStore,
  setAuditSubmitted,
  setSelectedCamera,
  setShowQuestion,
  setMutedParticipentContext,
  setMutedVideoParticipentContext,
} from "../../globalContext/remoteAudit/remoteAuditActions";

import {
  AudioMutedOutlined,
  PushpinOutlined,
  PushpinFilled,
  FormOutlined,
  PhoneOutlined,
  StopOutlined,
} from "@ant-design/icons";
import CreatePortalCard from "../CreatePortalCard";
import variables from "../../themes/base/styles/variables";
import { Button, Text } from "react_reusable_components";

import { userRole } from "../../constants";
import { AuthContext } from "../../globalContext/auth/authProvider";
import {
  formatDuration,
  getLocalizedMessage,
} from "../../UtilityFunctions/utils";
import { useParams } from "react-router-dom";
import useResponsive from "../../core/hooks/useResponsive";

const style = {
  remoteVideoContainer: {
    height: variables.remoteVideoContainerHeight,
    backgroundColor: variables.textDefault,
    width: variables.fullWidth,
    position: "relative",
  },
  videoContainer: {
    position: "absolute",
    width: "100%",
    height: "100%",
    backgroundColor: "#000",
    borderRadius: "6px",
    top: 0,
  },
  localVideoContainer: {
    right: "8px",
    bottom: "8px",
    position: "absolute",
    width: "130px",
    height: "130px",
    backgroundColor: "#4f4f4f",
    borderRadius: "6px",
    zIndex: 2,
  },
  parentGridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gridAutoRows:
      "minmax(100px, 0.4fr)" /* This will adjust the row height as well */,
    gridGap: "10px",
    justifyContent: "center" /* centers the grid horizontally */,
    alignContent: "center" /* centers the grid vertically */,
    height: "100%",
    backgroundColor: "black",
    padding: "12px",
  },
  childContainer: {
    display: "flex",
    position: "relative",
    justifyContent: "center" /* centers the grid horizontally */,
    alignContent: "center" /* centers the grid vertically */,
    backgroundColor:
      "#4f4f4f" /* change this to whatever color you want your squares to be */,
    borderRadius: "6px",
  },
  nameWrapper: {
    position: "absolute",
    bottom: "5px",
    left: "5px",
    color: "white",
    backgroundColor: "rgba(0,0,0,0.7)",
    padding: "4px",
    borderRadius: "4px",
    fontSize: "10px",
    // width: "min-content",
  },
  muteIconWrapper: {
    position: "absolute",
    fontSize: "18px",
    color: "white",
    zIndex: 3,
    top: "5px",
  },
  mainVideoContainer: {
    position: "absolute",
    top: 0,
    height: "calc(100% - 24px)",
    width: "calc(100% - 138px)",
    left: 0,
    backgroundColor: "#4f4f4f",
    margin: "12px",
    marginRight: "6px",
    borderRadius: "6px",
  },
  sideVideoContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
    gridGap: "10px",
    height: "100%",
    backgroundColor: "black",
    padding: "12px",
    width: "120px",
    gridAutoRows: "minmax(auto, 0.2fr)",
    placeContent: "end",
    float: "right",
  },
  muteIcon: {
    left: "5px",
  },
  pinIcon: {
    right: "5px",
  },
  requestAccessPopupStyle: {
    position: "absolute",
    zIndex: 999,
    backgroundColor: variables.bodyBg,
    display: "flex",
    right: 10,
    top: 80,
    padding: variables.paddingMedium,
    border: `1px solid ${variables.grey4}`,
    flexDirection: "column",
    gap: variables.gapSmall,
  },
  requestAccessTitle: {
    fontSize: variables.fontSizeSmall,
    lineHeight: variables.fontSizeXlarge,
    fontWeight: variables.fontWeightBold,
    textTransform: "capitalize",
  },
  buttonWrapper: {
    display: "flex",
    gap: variables.gapSmaller,
  },
  accessButtonStyle: {
    fontSize: variables.fontSizeSmall,
    lineHeight: variables.lineHeightNormal,
    textTransform: "capitalize",
    height: "unset",
    padding: `${variables.paddingSmall2} ${variables.paddingSmall}`,
    width: variables.requestAccessButtonWidth,
  },
  rejectButton: {
    border: `1px solid ${variables.grey4}`,
    backgroundColor: variables.primaryBg,
  },
  threeButtonsWrapper: {
    display: variables.dFlex,
    marginTop: variables.marginMedium,
    gap: variables.gapSmaller,
  },
  buttonRightWrapperStyle: {
    borderLeft: `1px solid ${variables.grey4}`,
    paddingLeft: variables.paddingSmall,
    gap: variables.gapSmaller,
    display: variables.dFlex,
  },
  customButtons: (backgroundColor) => ({
    backgroundColor: backgroundColor,
    border: variables.borderNone,
    height: variables.buttonHeightNormal,
    width: variables.buttonHeightNormal,
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
  }),
  endCallButtonStyle: {
    fontSize: variables.fontSizeNormal,
    color: variables.textPrimary,
    backgroundColor: variables.primaryBg,
    height: variables.buttonHeightNormal,
    fontWeight: variables.fontWeightNormal,
  },
  iconStyle: {
    width: variables.iconWidthLarge,
    height: variables.iconHeightLarge,
  },
  recordingInProgressWrapper: {
    position: "absolute",
    backgroundColor: variables.blurBackground,
    zIndex: 3,
    width: `calc(100% - ${variables.bottom} -${variables.bottom})`,
    top: variables.bottom,
    left: variables.bottom,
    right: variables.bottom,
    padding: `${variables.paddingSmallest} ${variables.paddingSmall}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  videoRecordingContainerStyle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: variables.paddingSmallest,
    borderRadius: "50px",
    background: "rgba(0, 0, 0, 0.44)",
    backdropFilter: "blur(7px)",
    width: "fit-content",
    color: variables.bodyBg,
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightNormal,
    gap: variables.gapXS,
  },
  flagBtnStyle: {
    border: `1px solid  ${variables.grey4}`,
    color: variables.bodyBg,
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    alignItems: "center",
    display: "flex",
    padding: `${variables.paddingSmallest} ${variables.paddingSmall}`,
  },
  redBorderStyle: { border: `2px solid  ${variables.primaryBg}` },
  greenBorderStyle: {
    border: `2px solid  ${variables.successGreen}`,
  },
  stopRecordingBtnStyle: {
    color: variables.primaryBg,
    backgroundColor: variables.bodyBg,
  },
  nameDisplayWrapper: {
    left: "0",
    right: 0,
    top: 0,
    bottom: 0,
    position: "absolute",
    margin: "auto",
    height: "50px",
    width: "50px",
    fontSize: "24px",
    color: "white",
    border: "1px solid",
    borderColor: variables.primaryBg,
    borderRadius: "50%",
    textAlign: "center",
    paddingTop: "11px",
    backgroundColor: variables.primaryBg,
  },
};

export default function RemoteVideoComponent() {
  const intl = useIntl();

  const [remoteAuditState, remoteAuditDispatch] =
    useContext(RemoteAuditContext);
  const [authState] = useContext(AuthContext);

  const [participantRefs, setParticipantRefs] = useState({});
  const [mutedParticipants, setMutedParticipants] = useState({});
  const [mutedVideoParticipants, setMutedVideoParticipants] = useState({});
  const [participantName, setParticipantName] = useState({});
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [accessRequests, setAccessRequests] = useState([]);
  const [dataTrack, setDataTrack] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isRedFlagClicked, setIsRedFlagClicked] = useState(false);
  const [isGreenFlagClicked, setIsGreenFlagClicked] = useState(false);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);

  const videoRefLocal = useRef(null);
  let joinRoomCalled = useRef(false);
  const greenFlagRef = useRef([]);
  const redFlagRef = useRef([]);
  const intervalRef = useRef(null);
  const { auditId } = useParams();
  const responsive = useResponsive();

  const setupConversationIfUserRoleIsTM = () => {
    if (remoteAuditState?.room?.userType === userRole.TM) {
      conversationSetup();
    }
  };

  useEffect(() => {
    if (remoteAuditState?.room?.token && !joinRoomCalled.current) {
      joinRoomCalled.current = true;
      joinRoom(remoteAuditState?.room);
      setupConversationIfUserRoleIsTM();
    }
  }, [remoteAuditState?.room]);

  useEffect(() => {
    remoteAuditDispatch(setMutedParticipentContext(mutedParticipants));
  }, [mutedParticipants]);

  useEffect(() => {
    remoteAuditDispatch(
      setMutedVideoParticipentContext(mutedVideoParticipants)
    );
  }, [mutedVideoParticipants]);

  useEffect(() => {
    if (!remoteAuditState?.isEvidenceCompleted) {
      intervalRef.current = setInterval(() => {
        setVideoDuration((prevDuration) => prevDuration + 1);
      }, 1000);
    }
  }, [remoteAuditState?.isEvidenceCompleted]);

  const handleConnectedState = async (client) => {
    const conversation = await client.getConversationBySid(
      remoteAuditState?.room?.conversationSid
    );
    if (!conversation) return;
    handleExistingMessages(conversation);
    handleNewMessages(conversation);
  };

  const handleExistingMessages = async (conversation) => {
    const messages = await conversation.getMessages();
    if (!messages?.items?.length) return;
    const msg = messages.items[messages.items.length - 1];
    if (msg?.state) {
      setAccessRequests([JSON.parse(msg.state.body)]);
    }
    messages.items.forEach((message) => {
      message.remove();
    });
  };

  const handleNewMessages = (conversation) => {
    conversation.on("messageAdded", (message) => {
      setAccessRequests([JSON.parse(message.state.body)]);
      message.remove();
    });
  };

  const conversationSetup = async () => {
    if (!remoteAuditState?.room?.conversationToken) return;
    const client = await new ConversationsClient(
      remoteAuditState?.room?.conversationToken
    );
    client.on("connectionStateChanged", (state) => {
      if (state === "connected") {
        handleConnectedState(client);
      }
    });
  };

  const handleIdentity = (participant) => {
    const identity = participant.identity.split("-");
    if (identity[0] === "DNSD ") {
      remoteAuditDispatch(setDnsdSid(participant.sid));
    }
    identity.pop();
    identity.join(" - ");
    return identity;
  };

  const handleParticipantTracks = (participant, newRef) => {
    participant.tracks.forEach((publication) => {
      if (publication.isSubscribed && publication.kind !== "data") {
        const track = publication.track;
        attachTrack(track, newRef);
        if (track.kind === "audio" && !publication.isTrackEnabled) {
          setMutedParticipants((prev) => ({
            ...prev,
            [participant.sid]: true,
          }));
        }
        if (track.kind === "video" && !publication.isTrackEnabled) {
          setMutedVideoParticipants((prev) => ({
            ...prev,
            [participant.sid]: true,
          }));
        }
      }
    });
  };

  const handleNoTracks = (participant) => {
    if (participant.audioTracks.size === 0) {
      setMutedParticipants((prev) => ({
        ...prev,
        [participant.sid]: true,
      }));
    }
    if (participant.videoTracks.size === 0) {
      setMutedVideoParticipants((prev) => ({
        ...prev,
        [participant.sid]: true,
      }));
    }
  };

  const handleTrackSubscribed = (participant, newRef) => {
    participant.on("trackSubscribed", (track) => {
      if (track.kind === "data") {
        track.on("message", (data) => {
          const requiredData = JSON.parse(data);
          remoteAuditDispatch(setCurrentQuestion(requiredData.currentQuestion));
          remoteAuditDispatch(
            setQuestionPayloadInStore(requiredData.questionPayloadInStore)
          );
          remoteAuditDispatch(setAuditSubmitted(requiredData.auditSubmitted));
        });
      } else {
        attachTrack(track, newRef);
      }
      if (track.kind === "video") {
        setMutedVideoParticipants((prev) => ({
          ...prev,
          [participant.sid]: false,
        }));
      }
    });
  };

  const handleTrackUnsubscribed = (participant) => {
    participant.on("trackUnsubscribed", (track) => {
      if (track.kind !== "data") {
        detachTrack(track);
      }
      if (track.kind === "video") {
        setMutedVideoParticipants((prev) => ({
          ...prev,
          [participant.sid]: true,
        }));
      }
    });
  };

  const handleTrackDisabled = (participant) => {
    participant.on("trackDisabled", (track) => {
      if (track.kind === "audio") {
        setMutedParticipants((prev) => ({
          ...prev,
          [participant.sid]: true,
        }));
      }
    });
  };

  const handleTrackEnabled = (participant) => {
    participant.on("trackEnabled", (track) => {
      if (track.kind === "audio") {
        setMutedParticipants((prev) => ({
          ...prev,
          [participant.sid]: false,
        }));
      }
    });
  };

  const handleParticipant = (participant, isNewParticipant = false) => {
    const identity = handleIdentity(participant);

    const newDiv = document.createElement("div");
    const newRef = React.createRef();
    newRef.current = newDiv;

    setParticipantRefs((prevRefs) => ({
      ...prevRefs,
      [participant.sid]: newRef,
    }));

    setParticipantName((prev) => ({
      ...prev,
      [participant.sid]: identity,
    }));

    handleParticipantTracks(participant, newRef);
    handleNoTracks(participant);
    handleTrackSubscribed(participant, newRef);
    handleTrackUnsubscribed(participant);
    handleTrackDisabled(participant);
    handleTrackEnabled(participant);
  };

  const handleParticipantDisconnect = (participant) => {
    participant.tracks.forEach((publication) => {
      if (publication.track && publication.kind !== "data") {
        detachTrack(publication.track);
      }
    });

    setParticipantRefs((prevRefs) => {
      const newRefs = { ...prevRefs };
      delete newRefs[participant.sid];
      return newRefs;
    });

    setParticipantName((prev) => {
      const newPrev = { ...prev };
      delete newPrev[participant.sid];
      return newPrev;
    });
    remoteAuditDispatch(setDnsdSid(null));
  };

  const attachTrack = (track, container) => {
    const element = track.attach();
    element.style.width = "100%";
    element.style.height = "100%";
    element.style.objectFit = "cover";
    element.style.borderRadius = "6px";
    container.current.appendChild(element);
  };

  const detachTrack = (track) => {
    track.detach().forEach((element) => element.remove());
  };

  const sendMessage = (message) => {
    if (dataTrack) {
      dataTrack.send(message);
    }
  };

  useEffect(() => {
    if (authState?.currentRole?.id === userRole.TM) {
      const message = {
        currentQuestion: remoteAuditState?.currentQuestion,
        questionPayloadInStore: remoteAuditState?.questionPayloadInStore,
        auditSubmitted: remoteAuditState?.auditSubmitted,
      };
      sendMessage(JSON.stringify(message));
    }
  }, [
    remoteAuditState?.currentQuestion,
    dataTrack,
    remoteAuditState?.questionPayloadInStore,
    remoteAuditState?.auditSubmitted,
  ]);

  const joinRoom = async (roomData) => {
    const localAudioTrack = await createLocalAudioTrack();
    const localDataTrack = new LocalDataTrack();

    setDataTrack(localDataTrack);

    const tracksItem = [localAudioTrack, localDataTrack];
    if (remoteAuditState?.video) {
      let localVideoTrack;
      if (remoteAuditState?.isMultipleCamera)
        localVideoTrack = await Video.createLocalVideoTrack({
          name: "camera",
          facingMode: remoteAuditState?.selectedCamera,
        });
      else localVideoTrack = await Video.createLocalVideoTrack();
      tracksItem.push(localVideoTrack);
    }

    Video.connect(roomData.token, {
      name: roomData.roomName,
      tracks: tracksItem,
    })
      .then(function (room) {
        remoteAuditDispatch(setConnectedRoom(room));

        room.localParticipant.videoTracks.forEach(function (publication) {
          const track = publication.track;
          attachTrack(track, videoRefLocal);
          setLocalVideoTrack(track);
        });

        if (!remoteAuditState?.audio) {
          room.localParticipant.audioTracks.forEach((publication) => {
            publication.track.disable();
          });
        }

        room.participants.forEach((participant) =>
          handleParticipant(participant)
        );
        room.on("participantConnected", (participant) =>
          handleParticipant(participant, true)
        );
        room.on("participantDisconnected", handleParticipantDisconnect);
        room.on("disconnected", (room) => {
          if (localVideoTrack) {
            localVideoTrack.track.stop();
            localVideoTrack.track
              .detach()
              .forEach((element) => element.remove());
          }
          remoteAuditDispatch(setCallEnded(true));
        });
      })
      .catch(function (error) {})//NOSONAR
  };

  const disconnect = () => {
    if (remoteAuditState.connectedRoom) {
      remoteAuditState.connectedRoom.localParticipant.tracks.forEach(
        (publication) => {
          if (publication.kind !== "data") {
            publication.track.stop();
            publication.track.detach().forEach((element) => element.remove());
          }
        }
      );
      remoteAuditState.connectedRoom.disconnect();
      setTimeout(() => remoteAuditDispatch(setCallEnded(true)));
    }
  };

  const toggleAudio = () => {
    if (remoteAuditState.connectedRoom) {
      if (!remoteAuditState?.audio) {
        remoteAuditState.connectedRoom.localParticipant.audioTracks.forEach(
          (publication) => {
            publication.track.enable();
          }
        );
      } else {
        remoteAuditState.connectedRoom.localParticipant.audioTracks.forEach(
          (publication) => {
            publication.track.disable();
          }
        );
      }
      remoteAuditDispatch(setAudioStatus(!remoteAuditState?.audio));
    }
  };

  const toggleVideo = async () => {
    if (remoteAuditState.connectedRoom) {
      if (!remoteAuditState?.video) {
        remoteAuditDispatch(setVideoStatus(!remoteAuditState?.video));
        let track;
        if (remoteAuditState?.isMultipleCamera)
          track = await Video.createLocalVideoTrack({
            name: "camera",
            facingMode: remoteAuditState?.selectedCamera,
          });
        else track = await Video.createLocalVideoTrack();
        remoteAuditState.connectedRoom.localParticipant.publishTrack(track);
        attachTrack(track, videoRefLocal);
        setLocalVideoTrack(track);
      } else {
        remoteAuditDispatch(setVideoStatus(!remoteAuditState?.video));
        remoteAuditState.connectedRoom.localParticipant.videoTracks.forEach(
          (publication) => {
            setLocalVideoTrack(null);
            publication.track.stop();
            publication.track.detach().forEach((element) => element.remove());
            publication.unpublish();
          }
        );
      }
    }
  };

  const handleToggleCameraDevice = async () => {
    const selectedCam =
      remoteAuditState?.selectedCamera === "user" ? "environment" : "user";
    remoteAuditDispatch(setSelectedCamera(selectedCam));
    if (remoteAuditState.connectedRoom.localParticipant.videoTracks) {
      remoteAuditState.connectedRoom.localParticipant.videoTracks.forEach(
        (publication) => {
          setLocalVideoTrack(null);
          publication.track.stop();
          publication.track.detach().forEach((element) => element.remove());
          publication.unpublish();
        }
      );
    }
    let track;
    if (remoteAuditState?.isMultipleCamera)
      track = await Video.createLocalVideoTrack({
        name: "camera",
        facingMode: selectedCam,
      });
    else track = await Video.createLocalVideoTrack();
    remoteAuditState.connectedRoom.localParticipant.publishTrack(track);
    attachTrack(track, videoRefLocal);
    setLocalVideoTrack(track);
  };

  const changeView = (id) => {
    if (selectedParticipant === id || selectedParticipant === "local") {
      setSelectedParticipant(null);
    } else {
      setSelectedParticipant(id);
    }
  };

  const allowParticipant = (isAccepted) => {
    AuditService.roomAccessToOthers({
      intl,
      body: {
        userIdentity: accessRequests[0]?.userIdentity,
        roomName: remoteAuditState?.room.roomName,
        isAccepted,
      },
    }).then(() => {
      const requests = accessRequests?.filter(
        (req) => req.userIdentity !== accessRequests[0]?.userIdentity
      );
      setAccessRequests(requests);
    });
  };

  const stopVideoRecording = () => {
    const body = {
      auditId: parseInt(auditId),
      roomName: remoteAuditState?.room?.roomName,
      recordingType: remoteAuditState?.evidenceInProgressInstance?.type,
      recordingAction: 0,
    };
    AuditService.recordEvidence({ intl, body }).then((res) => {
      remoteAuditDispatch(setIsEvidenceCompleted(true));
      clearInterval(intervalRef.current);
      setVideoDuration(0);
    });
  };

  const updateFlags = () => {
    AuditService.updateFlags({
      intl,
      documentId: remoteAuditState?.evidenceInProgressInstance?.documentId,
      body: {
        redFlag: redFlagRef.current,
        greenFlag: greenFlagRef.current,
        auditResponseId:
          remoteAuditState?.evidenceInProgressInstance?.responseId,
      },
    });
  };

  const onRedFlagClick = () => {
    setIsRedFlagClicked(true);
    redFlagRef.current.push(`${videoDuration}`);
    updateFlags();
    setTimeout(() => setIsRedFlagClicked(false), 100);
  };

  const onGreenFlagClick = () => {
    setIsGreenFlagClicked(true);
    greenFlagRef.current.push(`${videoDuration}`);
    updateFlags();
    setTimeout(() => setIsGreenFlagClicked(false), 100);
  };

  return (
    <div style={{ height: variables.remoteVideoContainerWrapperHeight }}>
      <div style={style.remoteVideoContainer}>
        {authState?.currentRole?.id === userRole.TM &&
          remoteAuditState?.evidenceInProgressInstance?.documentId &&
          !remoteAuditState?.isEvidenceCompleted && (
            <div style={style.recordingInProgressWrapper}>
              <div style={style.videoRecordingContainerStyle}>
                <img
                  alt="close"
                  src={"/icons/recording.svg"}
                  style={style.iconStyle}
                />
                {`${getLocalizedMessage(
                  intl,
                  remoteAuditState?.evidenceInProgressInstance?.type === "video"
                    ? "evidence.videoRecording"
                    : "evidence.audioRecording"
                )} | ${formatDuration(videoDuration)}`}
              </div>

              <div style={{ display: "flex", gap: variables.gapMediumSmall }}>
                {remoteAuditState?.evidenceInProgressInstance?.type ===
                  "video" && (
                  <>
                    <Button
                      style={{
                        ...style.flagBtnStyle,
                        ...(isRedFlagClicked && style.redBorderStyle),
                      }}
                      ghost
                      icon={<img src={"/icons/flag.svg"} alt="flag" />}
                      onClick={onRedFlagClick}
                    >
                      {responsive.isSm &&
                        getLocalizedMessage(intl, "evidence.redFlag")}
                    </Button>
                    <Button
                      style={{
                        ...style.flagBtnStyle,
                        ...(isGreenFlagClicked && style.greenBorderStyle),
                      }}
                      ghost
                      icon={<img src={"/icons/flag_green.svg"} alt="flag" />}
                      onClick={onGreenFlagClick}
                    >
                      {responsive.isSm &&
                        getLocalizedMessage(intl, "evidence.greenFlag")}
                    </Button>
                  </>
                )}
                <Button
                  style={{
                    ...style.flagBtnStyle,
                    ...style.stopRecordingBtnStyle,
                    ...(!responsive.isSm && { width: "unset" }),
                  }}
                  ghost
                  icon={<StopOutlined />}
                  onClick={() => {
                    stopVideoRecording();
                  }}
                >
                  {responsive.isSm &&
                    getLocalizedMessage(intl, "evidence.stopRecording")}
                </Button>
              </div>
            </div>
          )}
        <div
          style={{
            ...(Object.keys(participantRefs).length > 1
              ? selectedParticipant
                ? style.sideVideoContainer
                : style.parentGridContainer
              : style.participantContainer),
          }}
        >
          {Object.keys(participantRefs).map((participantId, index) => (
            <div
              id="video-local-participant"
              style={{
                ...(Object.keys(participantRefs).length > 1
                  ? selectedParticipant === participantId
                    ? style.mainVideoContainer
                    : style.childContainer
                  : style.videoContainer),
              }}
              ref={participantRefs[participantId]}
            >
              <div style={style.nameWrapper}>
                {participantName[participantId]}
              </div>

              {mutedVideoParticipants[participantId] && (
                <div style={style.nameDisplayWrapper}>
                  {participantName[participantId][0][0]}
                </div>
              )}

              {mutedParticipants[participantId] && (
                <div style={{ ...style.muteIconWrapper, ...style.muteIcon }}>
                  <AudioMutedOutlined />
                </div>
              )}
              {Object.keys(participantRefs).length > 1 && (
                <div
                  onClick={() => changeView(participantId)}
                  style={{ ...style.pinIcon, ...style.muteIconWrapper }}
                >
                  {selectedParticipant === participantId ? (
                    <PushpinFilled />
                  ) : (
                    <PushpinOutlined />
                  )}
                </div>
              )}
            </div>
          ))}
          <div
            style={{
              ...(Object.keys(participantRefs).length === 0
                ? style.videoContainer
                : Object.keys(participantRefs).length > 1
                ? selectedParticipant === "local"
                  ? style.mainVideoContainer
                  : style.childContainer
                : style.localVideoContainer),
            }}
            ref={videoRefLocal}
          >
            <div style={style.nameWrapper}>
              {remoteAuditState?.room?.userData?.name || authState?.data?.name}
            </div>
            {!remoteAuditState?.video && (
              <div style={style.nameDisplayWrapper}>
                {remoteAuditState?.room?.userData?.name[0] ||
                  authState?.data?.name[0]}
              </div>
            )}
            {!remoteAuditState?.audio && (
              <div style={{ ...style.muteIcon, ...style.muteIconWrapper }}>
                <AudioMutedOutlined />
              </div>
            )}
            {Object.keys(participantRefs).length > 1 && (
              <div
                onClick={() => changeView("local")}
                style={{ ...style.pinIcon, ...style.muteIconWrapper }}
              >
                {selectedParticipant === "local" ? (
                  <PushpinFilled />
                ) : (
                  <PushpinOutlined />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ ...style.threeButtonsWrapper }}>
          <Button
            style={style.endCallButtonStyle}
            ghost
            icon={!responsive.isSm && <PhoneOutlined />}
            onClick={disconnect}
          >
            {responsive.isSm && getLocalizedMessage(intl, "videoCall.endCall")}
          </Button>
          <div style={style.buttonRightWrapperStyle}>
            <Button
              onClick={toggleAudio}
              style={style.customButtons(
                remoteAuditState?.audio ? variables.bodyBg : variables.primaryBg
              )}
              icon={
                <img
                  style={style.iconStyle}
                  alt="mic-icon"
                  src={
                    remoteAuditState?.audio
                      ? "/icons/mic_on.svg"
                      : "/icons/mic_off.svg"
                  }
                />
              }
            />
            <Button
              disabled={!localVideoTrack && remoteAuditState?.video}
              style={style.customButtons(
                remoteAuditState?.video ? variables.bodyBg : variables.primaryBg
              )}
              onClick={toggleVideo}
              ghost
              icon={
                <img
                  style={style.iconStyle}
                  alt="camera-icon"
                  src={
                    remoteAuditState?.video
                      ? "/icons/video_camera_front.svg"
                      : "/icons/video_camera_front_off.svg"
                  }
                />
              }
            />
            {remoteAuditState?.isMultipleCamera &&
              localVideoTrack &&
              remoteAuditState?.video && (
                <Button
                  style={style.customButtons(
                    remoteAuditState?.video
                      ? variables.bodyBg
                      : variables.primaryBg
                  )}
                  onClick={handleToggleCameraDevice}
                  icon={
                    <img
                      style={style.iconStyle}
                      alt="camera-icon"
                      src={"/icons/camera_switch.svg"}
                    />
                  }
                />
              )}
          </div>
        </div>
        {!responsive?.isSm && (
          <Button
            style={{
              ...style.customButtons(
                remoteAuditState?.showQuestion
                  ? variables.bodyBg
                  : variables.primaryBg
              ),
              marginTop: variables.marginMediumLarge1,
              marginRight: "24%",
            }}
            icon={
              <FormOutlined
                style={{ color: remoteAuditState?.showQuestion && "#000" }}
              />
            }
            onClick={() => {
              remoteAuditDispatch(
                setShowQuestion(!remoteAuditState?.showQuestion)
              );
            }}
          />
        )}
      </div>
      {!!accessRequests?.length && (
        <CreatePortalCard id="root">
          <div style={style.requestAccessPopupStyle}>
            <Text
              style={style.requestAccessTitle}
              type="secondary"
            >{`${accessRequests[0]?.name} (${accessRequests[0]?.roleName}) wants to join?`}</Text>
            <div style={style.buttonWrapper}>
              <Button
                style={{
                  ...style.accessButtonStyle,
                  ...style.rejectButton,
                }}
                icon={null}
                onClick={() => allowParticipant(false)}
              >
                Reject
              </Button>
              <Button
                style={{
                  ...style.accessButtonStyle,
                  backgroundColor: variables.successGreen,
                }}
                icon={null}
                onClick={() => allowParticipant(true)}
              >
                Accept
              </Button>
            </div>
          </div>
        </CreatePortalCard>
      )}
    </div>
  );
}
